/**
 * B379 (c) — the storefront's side of the visit funnel: fire-and-forget
 * journey marks (visit / view / checkout) through `funnel.mark`.
 *
 * Telemetry never surfaces: a refusal (an older stage without the op, a
 * lost connection) is swallowed whole — no notice, no retry, no face.
 * Each stage is sent at most once per page life; the SERVER holds the
 * real dedup horizon (once per guest per UTC day), so a reload marking
 * again costs one refused count, never a double one. PAID is not in the
 * client vocabulary at all — the platform derives it from orders.
 */
import type { ApiClient } from "./api.js";

export type FunnelStageMark = "visit" | "view" | "checkout";

/** B525 — the shell this page is (hosted, inline embed, bare list, booking dialog). */
export type FunnelSurfaceMark = "hosted" | "inline" | "list" | "dialog";

export interface FunnelReporter {
  /** Send a stage mark once per page life; repeats are no-ops. */
  mark(stage: FunnelStageMark): void;
}

export function createFunnelReporter(options: {
  readonly api: ApiClient;
  readonly channelId: string;
  /**
   * B525 — named on every mark when given, so the Analytics funnel splits
   * by shell. Absent (the native parity fixture's reporter) sends the
   * original two fields, byte for byte.
   */
  readonly surface?: FunnelSurfaceMark;
}): FunnelReporter {
  const sent = new Set<FunnelStageMark>();
  return {
    mark(stage) {
      if (sent.has(stage)) return;
      sent.add(stage);
      void Promise.resolve()
        .then(() =>
          options.api.call("funnel.mark", {
            channelId: options.channelId,
            stage,
            ...(options.surface !== undefined ? { surface: options.surface } : {}),
          }),
        )
        .catch(() => {
          /* telemetry never surfaces (and never retries — the day horizon is the server's) */
        });
    },
  };
}
